/**
 * AdminDashboard Component
 *
 * Main landing page for site administrators. Fetches and displays a high-level
 * overview of ecosystem health: total organizations, connections, pending
 * approvals, and unowned organizations. Below the summary stats, it shows
 * previews of the five most recent pending organization submissions and
 * ownership claims, with links to the full review queues. A "Quick Actions"
 * section provides shortcuts to common admin tasks.
 *
 * Data is loaded in parallel from the graph and admin APIs on mount.
 *
 * Route: /admin (ProtectedRoute, admin role required)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useAdminApi } from '@/shared/services/adminApi';
import { useGraphApi } from '@/shared/services/graphApi';
import './Admin.css';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [pendingOrgs, setPendingOrgs] = useState([]);
    const [pendingTotal, setPendingTotal] = useState(0);
    const [pendingClaims, setPendingClaims] = useState([]);
    const [deadLinks, setDeadLinks] = useState([]);
    const [deadLinksError, setDeadLinksError] = useState(false);
    const [unownedCount, setUnownedCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const adminApi = useAdminApi();
    const graphApi = useGraphApi();

    // Load all dashboard data in parallel on mount
    useEffect(() => {
        const loadDashboardData = async () => {
            setLoading(true);
            // These admin API helpers take positional (page, limit) args.
            // Passing an object made axios serialize it as `page[limit]=5`,
            // which the server parsed as an object and turned into a NaN
            // skip -> 500. Pass page=1 with the desired preview limit.
            const [
                statsResult,
                pendingResult,
                claimsResult,
                unownedResult,
                deadLinksResult,
            ] = await Promise.allSettled([
                graphApi.fetchStats(),
                adminApi.fetchPendingOrganizations(1, 5),
                adminApi.fetchPendingClaims(1, 5),
                adminApi.fetchUnownedOrganizations(1, 1),
                adminApi.fetchDeadLinks(),
            ]);

            if (statsResult.status === 'fulfilled') {
                setStats(statsResult.value);
            } else {
                console.error(
                    'Failed to load graph stats:',
                    statsResult.reason
                );
            }

            if (pendingResult.status === 'fulfilled') {
                setPendingOrgs(pendingResult.value.organizations || []);
                setPendingTotal(pendingResult.value.pagination?.total || 0);
            } else {
                console.error(
                    'Failed to load pending organizations:',
                    pendingResult.reason
                );
            }

            if (claimsResult.status === 'fulfilled') {
                setPendingClaims(claimsResult.value.claims || []);
            } else {
                console.error(
                    'Failed to load pending claims:',
                    claimsResult.reason
                );
            }

            if (unownedResult.status === 'fulfilled') {
                setUnownedCount(unownedResult.value.pagination?.total || 0);
            } else {
                console.error(
                    'Failed to load unowned organizations:',
                    unownedResult.reason
                );
            }

            if (deadLinksResult.status === 'fulfilled') {
                setDeadLinks(deadLinksResult.value.checks || []);
            } else {
                setDeadLinksError(true);
                console.error(
                    'Failed to load dead links:',
                    deadLinksResult.reason
                );
            }

            setLoading(false);
        };

        loadDashboardData();
    }, [adminApi, graphApi]);

    /* Loading state: show spinner while API calls are in-flight */
    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | RILE Connect</title>
            </Helmet>
            <Container className="py-4">
                <h1 className="text-primary mb-4">Admin Dashboard</h1>

                {/* Stats Cards */}
                <Row className="mb-4">
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                                <h2 className="text-primary mb-0">
                                    {stats?.nodeCount || 0}
                                </h2>
                                <small className="text-muted">
                                    Total Organizations
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                                <h2 className="text-success mb-0">
                                    {stats?.edgeCount || 0}
                                </h2>
                                <small className="text-muted">
                                    Total Connections
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                                <h2 className="text-warning mb-0">
                                    {pendingTotal}
                                </h2>
                                <small className="text-muted">
                                    Pending Approvals
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="text-center">
                                <h2 className="text-info mb-0">
                                    {unownedCount}
                                </h2>
                                <small className="text-muted">
                                    Unowned Organizations
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* Pending Organizations */}
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                                <strong>Pending Organizations</strong>
                                <Link
                                    to="/admin/organizations"
                                    className="text-primary small"
                                >
                                    View All
                                </Link>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {pendingOrgs.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                        No pending organizations
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {pendingOrgs.map((org) => (
                                            <Link
                                                key={org._id}
                                                to={`/admin/organizations?review=${org._id}`}
                                                className="list-group-item list-group-item-action"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>
                                                            {org.name}
                                                        </strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {org.primarySector}
                                                        </small>
                                                    </div>
                                                    <Badge
                                                        bg="warning"
                                                        text="dark"
                                                    >
                                                        Pending
                                                    </Badge>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Pending Claims */}
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                                <strong>Ownership Claims</strong>
                                <Link
                                    to="/admin/claims"
                                    className="text-primary small"
                                >
                                    View All
                                </Link>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {pendingClaims.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                        No pending claims
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {pendingClaims.map((claim) => (
                                            <Link
                                                key={claim.claimId}
                                                to={`/admin/claims?review=${claim.organization._id}`}
                                                className="list-group-item list-group-item-action"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>
                                                            {
                                                                claim
                                                                    .organization
                                                                    .name
                                                            }
                                                        </strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            Claimed by:{' '}
                                                            {claim.userName ||
                                                                claim.user}
                                                        </small>
                                                    </div>
                                                    {claim.verificationMethod ===
                                                    'email_domain' ? (
                                                        <Badge bg="success">
                                                            Email Match
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary">
                                                            Manual
                                                        </Badge>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Dead Links */}
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <strong>Organization Dead Links</strong>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {deadLinksError ? (
                                    <div className="p-4 text-center text-muted">
                                        Unable to load dead link data.
                                    </div>
                                ) : deadLinks.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                        No dead links found.
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {deadLinks.map((link) => (
                                            <a
                                                key={link._id}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="list-group-item list-group-item-action"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>
                                                            {link.orgName}
                                                        </strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {link.url}
                                                        </small>
                                                    </div>
                                                    <Badge bg="danger">
                                                        {link.httpStatus ??
                                                            (link.errorMessage
                                                                ?.toLowerCase()
                                                                .includes(
                                                                    'timeout'
                                                                )
                                                                ? 'Timeout'
                                                                : 'Error')}
                                                    </Badge>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Quick Actions */}
                <Row>
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <strong>Quick Actions</strong>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-grid d-lg-flex flex-lg-wrap gap-2">
                                    <Link
                                        to="/admin/content/landing"
                                        className="btn btn-primary"
                                    >
                                        Edit Landing Page
                                    </Link>
                                    <Link
                                        to="/admin/content/about"
                                        className="btn btn-outline-primary"
                                    >
                                        Edit About Page
                                    </Link>
                                    <Link
                                        to="/admin/content/mission"
                                        className="btn btn-outline-primary"
                                    >
                                        Edit Mission Page
                                    </Link>
                                    <Link
                                        to="/admin/content/contact"
                                        className="btn btn-outline-primary"
                                    >
                                        Edit Contact Page
                                    </Link>
                                    <Link
                                        to="/admin/organizations"
                                        className="btn btn-outline-primary"
                                    >
                                        Review Pending Organizations
                                    </Link>
                                    <Link
                                        to="/admin/claims"
                                        className="btn btn-outline-primary"
                                    >
                                        Review Ownership Claims
                                    </Link>
                                    <Link
                                        to="/admin/organizations?filter=unowned"
                                        className="btn btn-outline-secondary"
                                    >
                                        Manage Unowned Organizations
                                    </Link>
                                    <Link
                                        to="/directory"
                                        className="btn btn-outline-secondary"
                                    >
                                        View Ecosystem
                                    </Link>
                                    <a
                                        href="https://analytics.google.com/analytics/web/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-secondary"
                                    >
                                        Google Analytics
                                    </a>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
